<script setup lang="ts">
import HarmonikaButton from './HarmonikaButton.vue'
import {PRECALC_DATA} from './instrumentData'

/**
 * Der komplette Knopf-Korpus (4 Reihen). Zwei Darstellungen über `variant`:
 * - `inspector` (Default): schlichter Rahmen für die Griff-Visualisierung (Ausgabe-Vorschau).
 * - `simulator`: dekorativer, vollflächiger Korpus (Holzmaserung, Schrauben, Carbon-Streifen) des
 *   Keyboard-Simulators.
 */
const {
  variant = 'inspector',
  activeButtons,
  previewButtons = new Set<string>(),
  activeMidiNotes = new Set<number>(),
  getLabel = () => '',
  getKeyLabel = () => '',
} = defineProps<{
  variant?: 'inspector' | 'simulator'
  activeButtons: Set<string>
  previewButtons?: Set<string>
  activeMidiNotes?: Set<number>
  getLabel?: (rowIdx: number, btnIdx: number) => string
  getKeyLabel?: (rowIdx: number, btnIdx: number) => string
}>()

const emit = defineEmits<{
  activate: [rowIdx: number, btnIdx: number]
  deactivate: [rowIdx: number, btnIdx: number]
}>()

const displayRows = [...PRECALC_DATA].reverse().map((row) => ({
  row,
  rowIdx: row.rowId - 1,
}))

function buttonId(rowIdx: number, btnIdx: number) {
  return `${rowIdx}-${btnIdx}`
}

// Die versetzte Anordnung der Reihen (wie auf einer echten Steirischen) entsteht über die
// `rowIndentSpace`-Platzhalter links/rechts der Knöpfe – NICHT über Rand-Klassen am Reihen-Div.
// Im Simulator skalieren die Platzhalter mit derselben Knopfgröße (Vielfache von `SIM_BTN`,
// siehe `HarmonikaButton.vue`), sonst verzerrt der Versatz, sobald die Knöpfe schrumpfen.
function rowIndentSpace(rowIdx: number) {
  if (variant === 'simulator') {
    switch (rowIdx) {
      case 1:
        return 'w-[calc(0.75*clamp(1.75rem,calc((100cqw_-_7rem)/13.67),3rem))]'
      case 2:
        return 'w-[calc(1.25*clamp(1.75rem,calc((100cqw_-_7rem)/13.67),3rem))]'
      case 3:
        return 'w-[calc(1.75*clamp(1.75rem,calc((100cqw_-_7rem)/13.67),3rem))]'
      default:
        return 'w-[calc(0.33*clamp(1.75rem,calc((100cqw_-_7rem)/13.67),3rem))]'
    }
  }
  switch (rowIdx) {
    case 1:
      return 'w-9'
    case 2:
      return 'w-15'
    case 3:
      return 'w-21'
    default:
      return 'w-4'
  }
}

function activate(rowIdx: number, btnIdx: number) {
  emit('activate', rowIdx, btnIdx)
}

function deactivate(rowIdx: number, btnIdx: number) {
  emit('deactivate', rowIdx, btnIdx)
}
</script>

<template>
  <!--
    `relative` ist Pflicht: der Verlauf unten liegt `absolute inset-0` und braucht diesen
    positionierten Vorfahren – sonst bezieht er sich auf den Viewport und legt sich dunkel über
    die GANZE Seite inkl. Header (weder `KeyboardView` noch `App.vue > main` sind positioniert).
    Die Section ist der horizontale Scroll-Container: `min-w-max` steht deshalb NUR an der
    inneren Spalte (an der Section selbst schlüge `min-width` den Scroll-Container aus, und der
    Elternteil schnitte die äußeren Knöpfe ab). `justify-center-safe` statt `justify-center`, sonst
    ist der linke Überlauf unerreichbar. `@container` liefert die `cqw`-Basis der Knopfgröße.
  -->
  <section
      class="@container relative flex overflow-x-auto justify-center-safe border-4 border-[#5d4037] bg-[#5d403755] shadow-[0_20px_50px_rgba(0,0,0,0.5)] h-full w-full rounded-[1.5rem] p-3 md:rounded-[2rem] md:p-5"

  >
    <div
        class="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/5 to-black/40 rounded-[1.3rem] md:rounded-[1.8rem]"

    />

    <div class="relative z-10 flex flex-col gap-1 py-2 md:gap-3   min-w-max grow">
      <div
          v-for="{row, rowIdx} in displayRows"
          :key="row.rowId"
          class="relative  flex flex-auto w-full    gap-1 md:gap-2 grow max-h-16 min-h-8"

      >
        <div :class="rowIndentSpace(rowIdx)" class="my-auto flex shrink-0 items-center justify-center text-[10px] font-bold text-white opacity-80">
          <span class="hidden md:inline">R{{ rowIdx + 1 }}</span>
        </div>

        <HarmonikaButton
            v-for="(buttonData, btnIdx) in row.buttons"
            :key="btnIdx"
            :variant="variant"
            :row-idx="rowIdx"
            :btn-idx="btnIdx"
            :btn-data="buttonData"
            :is-active="activeButtons.has(buttonId(rowIdx, btnIdx))"
            :is-preview="previewButtons.has(buttonId(rowIdx, btnIdx))"
            :label="getLabel(rowIdx, btnIdx)"
            :key-label="getKeyLabel(rowIdx, btnIdx)"
            :active-midi-notes="activeMidiNotes"
            @activate="activate"
            @deactivate="deactivate"
        />
        <div :class="rowIndentSpace(rowIdx)" class="my-auto flex shrink-0 items-center justify-center text-[10px] font-bold text-white opacity-80">
          <span class="hidden md:inline">R{{ rowIdx + 1 }}</span>
        </div>
      </div>
    </div>

  </section>
</template>
